import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadStart, uploadSuccess, uploadFailure } from "../../../../Redux/slices/jcSlice";
import { uploadJcApi, replaceJcApi } from "../../../../services/jcReview/uploadJcApi";
import useNotification from "@shared/hooks/useNotification";
import { CustomButton, CustomFileUpload } from "@shared/components";
import { Alert, Typography, Box } from "@mui/material";
import MonthYearSelector from "../shared/MonthYearSelector";
import ReplaceConfirmModal from "../shared/ReplaceConfirmModal";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/**
 * BMJcUpload — BM upload form for the BM-TSI JC Report.
 * Props:
 *   userCode       {string}
 *   userName       {string}
 *   salesGroupCode {string}
 *   division       {number}
 *   clusterCode    {string}
 *   userType       {string}  'SH3' or 'SH4'
 */
const BMJcUpload = ({ userCode, userEmail, userName, salesGroupCode, division, clusterCode, userType, onSuccess }) => {
  const dispatch = useDispatch();
  const { showSuccess, showError } = useNotification();
  const { submissions, uploadLoading } = useSelector((s) => s.jc);
  const sfToken = useSelector((s) => s?.tokens?.accessToken || "");

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [file, setFile] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);

  // Check if an active submission already exists for this user/month/year
  const existingSubmission = submissions.find(
    (s) => s.user_code === userCode && s.month === month && s.year === year && s.is_active
  );

  const handleFileChange = (files) => {
    if (files && files[0]) setFile(files[0]);
  };

  const handleSubmit = () => {
    if (!file) {
      showError("Please select a file to upload");
      return;
    }
    if (!month || !year) {
      showError("Please select a month and year");
      return;
    }

    // Guard: block past-month submissions (belt-and-suspenders alongside the selector)
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
      showError("Submissions are only allowed for the current month and future months.");
      return;
    }

    if (existingSubmission) {
      setShowReplaceModal(true);
      return;
    }

    doUpload();
  };

  const doUpload = async (replacing = false) => {
    setShowReplaceModal(false);
    dispatch(uploadStart());

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userCode", userCode);
    formData.append("userEmail", userEmail || "");
    formData.append("userName", userName);
    formData.append("role", userType || "SH3");
    formData.append("salesGroupCode", salesGroupCode || "");
    if (division) formData.append("division", String(division));
    formData.append("clusterCode", clusterCode || "");
    formData.append("month", String(month));
    formData.append("year", String(year));
    if (sfToken) formData.append("sfToken", sfToken);

    try {
      let res;
      if (replacing && existingSubmission) {
        res = await replaceJcApi(existingSubmission.id, formData);
      } else {
        res = await uploadJcApi(formData);
      }
      dispatch(uploadSuccess(res.data));
      showSuccess(`BM-TSI JC Report for ${MONTH_NAMES[month - 1]} ${year} ${replacing ? "replaced" : "uploaded"} successfully`);
      setFile(null);
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.msg || "Upload failed. Please try again.";
      dispatch(uploadFailure(msg));
      showError(msg);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
      {/* Month / Year */}
      <MonthYearSelector
        month={month}
        year={year}
        onMonthChange={setMonth}
        onYearChange={setYear}
        disabled={uploadLoading}
        disablePast
      />

      {existingSubmission && (
        <Alert severity="warning" sx={{ py: 0.5 }}>
          A BM-TSI JC Report is already submitted for {MONTH_NAMES[month - 1]} {year}. Uploading will replace it.
        </Alert>
      )}

      {/* File picker */}
      <Box>
        <Typography variant="body2" fontWeight={500} gutterBottom>
          Select File <span style={{ color: "#d32f2f" }}>*</span>
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            (PDF, Excel or Word, max 20 MB)
          </Typography>
        </Typography>
        <CustomFileUpload
          onChange={handleFileChange}
          accept=".pdf,.xlsx,.xls,.doc,.docx"
          maxSize={20}
          disabled={uploadLoading}
        />
      </Box>

      {/* Submit button */}
      <CustomButton
        onClick={handleSubmit}
        disabled={uploadLoading || !file}
        fullWidth
        loading={uploadLoading}
      >
        {uploadLoading ? "Submitting…" : existingSubmission ? "Replace" : "Submit"}
      </CustomButton>
      <ReplaceConfirmModal
        isOpen={showReplaceModal}
        onClose={() => setShowReplaceModal(false)}
        onConfirm={() => doUpload(true)}
        loading={uploadLoading}
        month={MONTH_NAMES[month - 1]}
        year={year}
      />
    </Box>
  );
};

export default BMJcUpload;
